import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Trash2, Plus, RefreshCw } from "lucide-react";
import { API, PA_BASE } from "../config";

const STATUSES = ["New", "Contacted", "Qualified", "Quote Sent", "Booked", "Completed", "Lost"];

const inputCls =
  "w-full bg-transparent border-b border-white/20 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-white focus:outline-none transition-colors duration-300";

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const LeadsTab = ({ token, onAuthFail }) => {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("All");
  const [notesDraft, setNotesDraft] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leads`, authHeaders(token));
      setLeads(res.data);
    } catch (e) {
      if (e.response?.status === 401) onAuthFail();
    }
  }, [token, onAuthFail]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id, patch) => {
    try {
      await axios.patch(`${API}/leads/${id}`, patch, authHeaders(token));
      load();
    } catch (e) {
      if (e.response?.status === 401) onAuthFail();
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, authHeaders(token));
      load();
    } catch (e) {
      if (e.response?.status === 401) onAuthFail();
    }
  };

  if (!leads) return <div className="py-20 text-white/40 text-sm">Loading enquiries…</div>;

  const visible = filter === "All" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div data-testid="leads-tab">
      <div className="flex flex-wrap items-center gap-2">
        {["All", ...STATUSES].map((s) => (
          <button
            key={s}
            data-testid={`lead-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilter(s)}
            className={`border px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
              filter === s ? "border-white bg-white text-black" : "border-white/20 text-white/60 hover:border-white/60"
            }`}
          >
            {s} {s !== "All" && `(${leads.filter((l) => l.status === s).length})`}
          </button>
        ))}
        <button onClick={load} data-testid="leads-refresh" className="ml-auto p-2 text-white/50 hover:text-white transition-colors duration-300" aria-label="Refresh">
          <RefreshCw size={15} />
        </button>
      </div>

      {visible.length === 0 && (
        <p className="mt-10 text-sm text-white/40" data-testid="leads-empty">No enquiries in this state.</p>
      )}

      <div className="mt-8 space-y-4">
        {visible.map((l) => (
          <article key={l.id} data-testid={`lead-card-${l.id}`} className="border border-white/10 bg-white/[0.03] p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-display text-xl text-white">{l.name}</p>
                <p className="mt-1 text-sm text-white/50">
                  {l.email}
                  {l.phone ? ` · ${l.phone}` : ""}
                  {l.whatsapp ? ` · WA: ${l.whatsapp}` : ""}
                  {l.company ? ` · ${l.company}` : ""}
                </p>
              </div>
              <select
                data-testid={`lead-status-${l.id}`}
                value={l.status}
                onChange={(e) => update(l.id, { status: e.target.value })}
                className="border border-white/20 bg-transparent px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-white focus:outline-none [&>option]:text-black"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-1 text-[13px] text-white/65">
              {l.departure && <span>{l.departure} → {l.destination || "—"}</span>}
              {l.departure_date && <span>{l.departure_date}{l.return_date ? ` → ${l.return_date}` : ""}</span>}
              {l.trip_type && <span className="capitalize">{l.trip_type.replace("-", " ")}</span>}
              {l.passengers && <span>{l.passengers} pax</span>}
              {l.preferred_aircraft && <span>{l.preferred_aircraft}</span>}
              <span className="text-white/35">{l.source} · {new Date(l.submitted_at).toLocaleString()}</span>
            </div>
            {l.special_requirements && (
              <p className="mt-3 text-[13px] leading-relaxed text-white/55">“{l.special_requirements}”</p>
            )}
            <div className="mt-4 flex items-end gap-3">
              <input
                data-testid={`lead-notes-${l.id}`}
                value={notesDraft[l.id] ?? l.notes ?? ""}
                onChange={(e) => setNotesDraft({ ...notesDraft, [l.id]: e.target.value })}
                placeholder="Internal notes…"
                className={`${inputCls} flex-1`}
              />
              <button
                data-testid={`lead-notes-save-${l.id}`}
                onClick={() => update(l.id, { notes: notesDraft[l.id] ?? l.notes ?? "" })}
                className="border border-white/20 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-white/70 transition-colors duration-300 hover:bg-white hover:text-black"
              >
                Save note
              </button>
              <button
                data-testid={`lead-delete-${l.id}`}
                onClick={() => remove(l.id)}
                className="p-2 text-white/35 transition-colors duration-300 hover:text-red-400"
                aria-label="Delete lead"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

const EMPTY_LEG = {
  aircraft: "", departure: "", destination: "", date: "", time: "",
  passenger_capacity: "", price: "", operator: "", availability: "On request", expiry: "",
};

const EmptyLegsTab = ({ token, onAuthFail }) => {
  const [legs, setLegs] = useState(null);
  const [form, setForm] = useState(EMPTY_LEG);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/empty-legs`);
      setLegs(res.data);
    } catch {
      setLegs([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await axios.post(`${API}/empty-legs`, payload, authHeaders(token));
      setForm(EMPTY_LEG);
      load();
    } catch (err) {
      if (err.response?.status === 401) onAuthFail();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this empty leg?")) return;
    try {
      await axios.delete(`${API}/empty-legs/${id}`, authHeaders(token));
      load();
    } catch (e) {
      if (e.response?.status === 401) onAuthFail();
    }
  };

  return (
    <div className="grid grid-cols-1 gap-12 lg:grid-cols-2" data-testid="empty-legs-tab">
      <form onSubmit={submit} data-testid="admin-empty-leg-form" className="border border-white/10 bg-white/[0.03] p-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Publish an empty leg</p>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <input required data-testid="el-aircraft" placeholder="Aircraft *" value={form.aircraft} onChange={set("aircraft")} className={inputCls} />
          <input data-testid="el-operator" placeholder="Operator" value={form.operator} onChange={set("operator")} className={inputCls} />
          <input required data-testid="el-departure" placeholder="Departure *" value={form.departure} onChange={set("departure")} className={inputCls} />
          <input required data-testid="el-destination" placeholder="Destination *" value={form.destination} onChange={set("destination")} className={inputCls} />
          <input required data-testid="el-date" type="date" value={form.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} />
          <input data-testid="el-time" placeholder="Time (e.g. 14:30 LT)" value={form.time} onChange={set("time")} className={inputCls} />
          <input data-testid="el-capacity" placeholder="Passenger capacity" value={form.passenger_capacity} onChange={set("passenger_capacity")} className={inputCls} />
          <input data-testid="el-price" placeholder="Price (e.g. USD 18,500)" value={form.price} onChange={set("price")} className={inputCls} />
          <select data-testid="el-availability" value={form.availability} onChange={set("availability")} className={`${inputCls} [&>option]:text-black`}>
            <option>On request</option>
            <option>Available</option>
            <option>Provisional</option>
          </select>
          <input data-testid="el-expiry" type="date" value={form.expiry} onChange={set("expiry")} className={`${inputCls} [color-scheme:dark]`} title="Expiry" />
        </div>
        <button
          type="submit"
          data-testid="el-submit"
          disabled={saving}
          className="mt-8 inline-flex items-center gap-2 bg-white px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white/85 disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Publish
        </button>
      </form>

      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Live listings</p>
        {!legs ? (
          <p className="mt-6 text-sm text-white/40">Loading…</p>
        ) : legs.length === 0 ? (
          <p className="mt-6 text-sm text-white/40" data-testid="el-none">Nothing published yet.</p>
        ) : (
          <div className="mt-6 space-y-3" data-testid="el-list">
            {legs.map((leg) => (
              <div key={leg.id} data-testid={`el-item-${leg.id}`} className="flex items-center justify-between gap-4 border border-white/10 bg-white/[0.03] p-5">
                <div>
                  <p className="text-sm text-white">{leg.departure} → {leg.destination} · {leg.date}</p>
                  <p className="mt-1 text-[12px] text-white/45">
                    {leg.aircraft}{leg.passenger_capacity ? ` · ${leg.passenger_capacity} pax` : ""}{leg.price ? ` · ${leg.price}` : ""} · {leg.availability}
                  </p>
                </div>
                <button data-testid={`el-delete-${leg.id}`} onClick={() => remove(leg.id)} className="p-2 text-white/35 transition-colors duration-300 hover:text-red-400" aria-label="Delete empty leg">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminPage() {
  useEffect(() => {
    document.title = "Charter Desk — JS Private Aviation";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
  const [token, setToken] = useState(() => sessionStorage.getItem("jspa_admin") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("leads");

  const onAuthFail = useCallback(() => {
    sessionStorage.removeItem("jspa_admin");
    setToken("");
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("jspa_admin", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-5" data-testid="admin-login-page">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm border border-white/10 bg-white/[0.03] p-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">JS Private Aviation</p>
          <h1 className="mt-3 font-display text-3xl tracking-tight text-white">Charter desk</h1>
          <input
            type="password"
            required
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className={`${inputCls} mt-8`}
          />
          {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={busy}
            className="mt-8 w-full bg-white py-3.5 text-[11px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85 disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <Link to={PA_BASE} className="mt-6 block text-center text-[12px] text-white/40 transition-colors duration-300 hover:text-white">
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-5 py-10 sm:px-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1200px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">JS Private Aviation</p>
            <h1 className="mt-2 font-display text-3xl tracking-tight text-white">Charter desk</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to={PA_BASE} data-testid="admin-view-site" className="border border-white/20 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-white/60 transition-colors duration-300 hover:text-white">
              View site
            </Link>
            <button
              onClick={onAuthFail}
              data-testid="admin-sign-out"
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-white/60 transition-colors duration-300 hover:text-white"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-10 flex gap-2 border-b border-white/10 pb-px">
          {[
            ["leads", "Charter enquiries"],
            ["empty-legs", "Empty legs"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === key ? "border-b border-white text-white" : "text-white/45 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-10 pb-20">
          {tab === "leads" ? (
            <LeadsTab token={token} onAuthFail={onAuthFail} />
          ) : (
            <EmptyLegsTab token={token} onAuthFail={onAuthFail} />
          )}
        </div>
      </div>
    </div>
  );
}
